import React from 'react';
import { FileText, Download, ExternalLink, Scale, FileSignature } from 'lucide-react';
import { Button } from '../../../ui/button';

const DocumentsTab = ({ caseData }) => {
  // Collect all documents including legal documents
  const allDocuments = [];
  
  // Add Abtretungserklärung if exists
  if (caseData?.abtretungserklaerung_url) {
    allDocuments.push({
      type: 'abtretungserklaerung',
      label: 'Abtretungserklärung',
      url: caseData.abtretungserklaerung_url,
      uploaded_at: caseData.abtretungserklaerung_uploaded_at,
      icon: FileSignature,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    });
  }
  
  // Add Vollmacht if exists
  if (caseData?.vollmacht_url) {
    allDocuments.push({
      type: 'vollmacht',
      label: 'Vollmacht (Anwalt)',
      url: caseData.vollmacht_url,
      uploaded_at: caseData.vollmacht_uploaded_at,
      icon: Scale,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    });
  }
  
  // Add documents from the documents array
  if (caseData?.documents && Array.isArray(caseData.documents)) {
    caseData.documents.forEach((doc, idx) => {
      // Skip if already added via dedicated fields
      if (doc.type === 'abtretungserklaerung' && caseData.abtretungserklaerung_url) return;
      if (doc.type === 'vollmacht' && caseData.vollmacht_url) return;
      
      allDocuments.push({
        type: doc.type || 'document',
        label: doc.filename || doc.original_filename || `Dokument ${idx + 1}`,
        url: doc.url || doc,
        uploaded_at: doc.uploaded_at,
        icon: FileText,
        color: 'text-gray-600',
        bgColor: 'bg-gray-50'
      });
    });
  }
  
  // Handle download - supports both URLs and base64
  const handleDownload = (doc) => {
    const url = doc.url;
    
    // Check if it's a base64 data URL
    if (url.startsWith('data:')) {
      // Create a link to download base64 PDF
      const link = document.createElement('a');
      link.href = url;
      link.download = `${doc.type}_${caseData.id?.substring(0, 8) || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // Regular URL - open in new tab
      window.open(`${process.env.REACT_APP_BACKEND_URL}${url}`, '_blank');
    }
  };
  
  // Handle view in new tab
  const handleView = (doc) => {
    const url = doc.url;
    
    if (url.startsWith('data:')) {
      // Open base64 PDF in new tab
      const newWindow = window.open();
      newWindow.document.write(`
        <html>
          <head><title>${doc.label}</title></head>
          <body style="margin:0">
            <embed src="${url}" type="application/pdf" width="100%" height="100%">
          </body>
        </html>
      `);
    } else {
      window.open(`${process.env.REACT_APP_BACKEND_URL}${url}`, '_blank');
    }
  };

  if (allDocuments.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 font-medium">Keine Dokumente vorhanden</p>
        <p className="text-sm text-gray-400 mt-1">
          Rechtsdokumente wie Abtretungserklärung und Vollmacht werden hier angezeigt
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Legal Documents Section */}
      {allDocuments.filter(d => d.type === 'abtretungserklaerung' || d.type === 'vollmacht').length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Rechtsdokumente
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {allDocuments.filter(d => d.type === 'abtretungserklaerung' || d.type === 'vollmacht').map((doc, idx) => {
              const IconComponent = doc.icon;
              return (
                <div 
                  key={`legal-${idx}`} 
                  className={`${doc.bgColor} border border-gray-200 rounded-xl p-4`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 bg-white rounded-lg shadow-sm`}>
                      <IconComponent className={`w-6 h-6 ${doc.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900">{doc.label}</p>
                      {doc.uploaded_at && (
                        <p className="text-xs text-gray-500 mt-1">
                          Hochgeladen: {new Date(doc.uploaded_at).toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      )}
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleView(doc)}
                          className="text-xs"
                          data-testid={`view-${doc.type}`}
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Anzeigen
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleDownload(doc)}
                          className="text-xs bg-brand-green hover:bg-brand-green-dark text-white"
                          data-testid={`download-${doc.type}`}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Other Documents Section */}
      {allDocuments.filter(d => d.type !== 'abtretungserklaerung' && d.type !== 'vollmacht').length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Weitere Dokumente
          </h3>
          <div className="space-y-3">
            {allDocuments.filter(d => d.type !== 'abtretungserklaerung' && d.type !== 'vollmacht').map((doc, idx) => {
              const IconComponent = doc.icon || FileText;
              return (
                <div 
                  key={`other-${idx}`} 
                  className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl p-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg">
                      <IconComponent className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{doc.label}</p>
                      {doc.uploaded_at && (
                        <p className="text-xs text-gray-500">
                          {new Date(doc.uploaded_at).toLocaleDateString('de-DE')}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleView(doc)}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDownload(doc)}
                      className="text-brand-green hover:text-brand-green-dark"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsTab;
